import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import { Box, Tab } from '@mui/material'
import { useEffect, useState } from 'react'
import useAuth from '../../../hooks/useAuth'
import { CompanyIssues } from './CompanyIssues'
import { CompanyWarrants } from './CompanyWarrants'
import React from 'react'
import { sortAndSetIssuesFunction } from './CompanyTargets'
import IssueApi from '../../../api/IssueApi'
import WarrantApi from '../../../api/WarrantApi'
import ConvertiblesApi from '../../../api/ConvertiblesApi'
import { CompanyConvertibles } from './CompanyConvertibles'

const issueApi = new IssueApi()
const warrantApi = new WarrantApi()
const convertibleApi = new ConvertiblesApi()

export const CompanyEvents = ({
    setEntityType,
    company,
    selectedTargets,
    associatedPRs,
    setAssociatedPRs,
    issues,
    setIssues,
    sourceType,
    setSourceType,
    selectedPR,
    onArchivePR,
}) => {
    const { isAuthenticated } = useAuth()
    const [warrants, setWarrants] = useState(null)
    const [convertibles, setConvertibles] = useState(null)
    const [selectedTab, setSelectedTab] = useState('issues')

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            const result = await issueApi.findByCompanyId(company.id, false)
            setIssues(sortAndSetIssuesFunction(result))
        }
        fetchData()
    }, [isAuthenticated, company, setIssues])

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            const result = await warrantApi.findByCompanyId(company.id)
            // keep only one of each warrant (by id)
            const uniqueWarrants = result.filter((w, i) => result.findIndex((w2) => w2.id === w.id) === i)

            uniqueWarrants.sort((a, b) => {
                // Get the newest subEndDate for warrant `a`
                const s1 =
                    a.subscriptionPeriods && a.subscriptionPeriods.length
                        ? new Date(Math.max(...a.subscriptionPeriods.map((period) => new Date(period.subEndDate))))
                        : null

                // Get the newest subEndDate for warrant `b`
                const s2 =
                    b.subscriptionPeriods && b.subscriptionPeriods.length
                        ? new Date(Math.max(...b.subscriptionPeriods.map((period) => new Date(period.subEndDate))))
                        : null

                // Sort by newest subEndDate first
                if (s1 === null && s2 === null) return 0 // Both are null, they are equal
                if (s1 === null) return 1 // `a` has no date, `b` should come first
                if (s2 === null) return -1 // `b` has no date, `a` should come first

                return s2 - s1 // Newest dates first
            })

            setWarrants(uniqueWarrants)
        }
        fetchData()
    }, [isAuthenticated, company, setWarrants])

    useEffect(() => {
        if (!isAuthenticated || !company) return

        const fetchData = async () => {
            const result = await convertibleApi.findByCompanyId(company.id)
            setConvertibles(result)
        }
        fetchData()
    }, [isAuthenticated, company, setConvertibles])

    if (company === null) return null

    const handleTabChange = (newValue) => {
        setSelectedTab(newValue)
        if (newValue === 'convertibles') {
            setEntityType('Convertible')
        } else {
            setEntityType(null)
        }
    }

    // return content
    return (
        <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList value={selectedTab} onChange={(e, newValue) => handleTabChange(newValue)}>
                    <Tab label='Emissioner' value='issues' />
                    <Tab label='Teckningsoptioner' value='warrants' />
                    <Tab label='Konvertibler' value='convertibles' />
                </TabList>
            </Box>
            <TabPanel value='issues'>
                <CompanyIssues
                    company={company}
                    selectedTargets={selectedTargets}
                    associatedPRs={associatedPRs}
                    setAssociatedPRs={setAssociatedPRs}
                    issues={issues}
                    setIssues={setIssues}
                    sourceType={sourceType}
                    setSourceType={setSourceType}
                    selectedPR={selectedPR}
                    onArchivePR={onArchivePR}
                />
            </TabPanel>
            <TabPanel value='warrants'>
                <CompanyWarrants
                    selectedTargets={selectedTargets}
                    associatedPRs={associatedPRs}
                    setAssociatedPRs={setAssociatedPRs}
                    warrants={warrants}
                    sourceType={sourceType}
                    selectedPR={selectedPR}
                    onArchivePR={onArchivePR}
                />
            </TabPanel>
            <TabPanel value='convertibles'>
                <CompanyConvertibles
                    company={company}
                    selectedTargets={selectedTargets}
                    associatedPRs={associatedPRs}
                    setAssociatedPRs={setAssociatedPRs}
                    convertibles={convertibles}
                    setConvertibles={setConvertibles}
                    sourceType={sourceType}
                    setSourceType={setSourceType}
                    selectedPR={selectedPR}
                    onArchivePR={onArchivePR}
                />
            </TabPanel>
        </TabContext>
    )
}
